import { useState, type FormEvent } from 'react'
import { useSignIn } from '@clerk/clerk-react'
import { Link, useNavigate } from '@tanstack/react-router'
import {
  ArrowLeft,
  ArrowRight,
  Eye,
  EyeOff,
  KeyRound,
  Lock,
  Mail,
} from 'lucide-react'
import { cn } from '@/lib/cn'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { AuthLayout } from '@/features/auth/components/AuthLayout'
import { AuthNotice, useAuthNotice } from '@/features/auth/components/AuthNotice'
import {
  describeClerkError,
  isEnumerationError,
  PASSWORD_RESET_ENUMERATION_CODES,
} from '@/features/auth/lib/clerkErrors'
import styles from './PasswordResetPage.module.scss'

type Step = 'request' | 'verify'

const RESET_ERROR_OVERRIDES = {
  form_code_incorrect: 'Código inválido. Verifique e tente novamente.',
  verification_expired: 'O código expirou. Solicite um novo.',
} as const

export function PasswordResetPage() {
  const [step, setStep] = useState<Step>('request')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const { notice, showError, showInfo, clear } = useAuthNotice()
  const [submitting, setSubmitting] = useState(false)

  const { signIn, isLoaded, setActive } = useSignIn()
  const navigate = useNavigate()

  async function handleRequestCode(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    // Sem `!signIn`: os tipos do Clerk são união discriminada por `isLoaded`,
    // então o early return já estreita `signIn` para não-nulo. O check extra
    // era morto e o eslint o via como sempre falso.
    if (!isLoaded || submitting) return
    clear()
    setSubmitting(true)
    try {
      await signIn.create({
        strategy: 'reset_password_email_code',
        identifier: email,
      })
    } catch (err) {
      // Enumeration-sensitive codes (e.g. "email not found") fall through
      // to the generic info notice below — same visible outcome whether
      // the email exists or not, so an attacker can't probe. Real errors
      // (rate limit, network, etc.) surface normally.
      if (!isEnumerationError(err, PASSWORD_RESET_ENUMERATION_CODES)) {
        showError(describeClerkError(err, 'Falha ao solicitar o código. Tente novamente.'))
        setSubmitting(false)
        return
      }
    }
    // Always advance to step "verify" with a generic info message — same
    // visual outcome whether the email exists or not. If it doesn't exist,
    // the code they enter next will simply fail at the verify step.
    setStep('verify')
    showInfo(
      'Verifique seu email',
      `Se ${email} estiver registrado, enviamos um código de 6 dígitos. Confira a caixa de entrada e o spam.`,
    )
    setSubmitting(false)
  }

  async function handleVerifyAndReset(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    // Sem `!signIn`: os tipos do Clerk são união discriminada por `isLoaded`,
    // então o early return já estreita `signIn` para não-nulo. O check extra
    // era morto e o eslint o via como sempre falso.
    if (!isLoaded || submitting) return
    clear()
    setSubmitting(true)
    try {
      const result = await signIn.attemptFirstFactor({
        strategy: 'reset_password_email_code',
        code,
        password: newPassword,
      })
      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId })
        await navigate({ to: '/' })
      } else {
        // Some configurations (MFA enabled) require a second factor before
        // sign-in completes. Surface what's needed instead of pretending
        // it worked.
        showError(`Etapa adicional necessária (status: ${String(result.status)}).`)
      }
    } catch (err) {
      showError(
        describeClerkError(err, 'Não foi possível redefinir a senha.', {
          overrides: RESET_ERROR_OVERRIDES,
        }),
      )
    } finally {
      setSubmitting(false)
    }
  }

  function backToRequest() {
    setStep('request')
    setCode('')
    setNewPassword('')
    clear()
  }

  return (
    <AuthLayout>
      <h1 className={styles.title}>
        {step === 'request' ? 'Recuperar senha' : 'Verifique seu email'}
      </h1>
      <p className={styles.sub}>
        {step === 'request'
          ? 'Vamos te enviar um código pra redefinir sua senha.'
          : 'Cole o código que chegou no seu email e escolha uma senha nova.'}
      </p>

      {notice ? <AuthNotice notice={notice} onClose={clear} /> : null}

      {step === 'request' ? (
        <form onSubmit={handleRequestCode} noValidate>
          <div className={styles.field}>
            <label htmlFor="reset-email" className={styles.label}>
              Email
            </label>
            <Input
              id="reset-email"
              type="email"
              placeholder="voce@exemplo.com"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => { setEmail(e.target.value); }}
              leadingIcon={<Mail />}
              className={styles.input}
            />
          </div>

          <Button
            type="submit"
            variant="primary"
            block
            trailingIcon={<ArrowRight />}
            className={styles.submitBtn}
            disabled={!isLoaded || submitting || !email}
          >
            Enviar código
          </Button>

          <div className={styles.footRow}>
            <Link to="/login" className={styles.link}>
              <ArrowLeft className={styles.linkIcon} aria-hidden />
              Voltar pro login
            </Link>
          </div>
        </form>
      ) : (
        <form onSubmit={handleVerifyAndReset} noValidate>
          <div className={styles.field}>
            <label htmlFor="reset-code" className={styles.label}>
              Código de verificação
            </label>
            <Input
              id="reset-code"
              type="text"
              placeholder="000000"
              autoComplete="one-time-code"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              required
              value={code}
              onChange={(e) => { setCode(e.target.value.replace(/\D/g, '')); }}
              leadingIcon={<KeyRound />}
              className={cn(styles.input, styles.inputCode)}
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="reset-new-password" className={styles.label}>
              Nova senha
            </label>
            <Input
              id="reset-new-password"
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              autoComplete="new-password"
              required
              value={newPassword}
              onChange={(e) => { setNewPassword(e.target.value); }}
              leadingIcon={<Lock />}
              trailingIcon={
                <button
                  type="button"
                  className={styles.eyeBtn}
                  onClick={() => { setShowPassword((v) => !v); }}
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  aria-pressed={showPassword}
                >
                  {showPassword ? <EyeOff /> : <Eye />}
                </button>
              }
              className={styles.input}
            />
          </div>

          <Button
            type="submit"
            variant="primary"
            block
            trailingIcon={<ArrowRight />}
            className={styles.submitBtn}
            disabled={!isLoaded || submitting || code.length < 6 || !newPassword}
          >
            Redefinir senha
          </Button>

          <div className={styles.footRow}>
            <button type="button" className={styles.linkBtn} onClick={backToRequest}>
              <ArrowLeft className={styles.linkIcon} aria-hidden />
              Usar outro email
            </button>
          </div>
        </form>
      )}
    </AuthLayout>
  )
}
